import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { CalendarDays, Flag, ChevronLeft, ChevronRight, Info, PartyPopper, Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Badge, Empty, Table, Td, Input, Label, Select } from "../components/UI";
import { SlidePanel } from "../components/SlidePanel";

const dayName = (iso) => dayjs(iso).format("dddd");

const CATEGORIES = [
  ["staff_retreat", "Staff Retreat"],
  ["cultural", "Cultural"],
  ["religious", "Religious"],
  ["community", "Community"],
  ["training", "Training"],
  ["other", "Other"],
];

const EMPTY_CLINIC = {
  name: "", description: "",
  start_date: dayjs().format("YYYY-MM-DD"),
  end_date: dayjs().format("YYYY-MM-DD"),
  category: "other", color: "#7E57C2", closes_clinic: true,
};

export default function Holidays({ embedded = false }) {
  const [year, setYear] = useState(dayjs().year());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clinicHolidays, setClinicHolidays] = useState([]);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | id
  const [form, setForm] = useState(EMPTY_CLINIC);
  const [busy, setBusy] = useState(false);

  const isAdmin = !!me && (me.role === "admin" || me.is_super_admin);

  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/holidays", { params: { year } }),
      api.get("/clinic-holidays", { params: { start: `${year}-01-01`, end: `${year}-12-31` } }).catch(() => ({ data: [] })),
    ])
      .then(([h, c]) => { setHolidays(h.data.holidays || []); setClinicHolidays(c.data || []); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [year]);

  const reloadClinic = () => {
    api.get("/clinic-holidays", { params: { start: `${year}-01-01`, end: `${year}-12-31` } })
      .then((r) => setClinicHolidays(r.data || []))
      .catch((e) => toast.error(apiError(e)));
  };

  const today = dayjs().format("YYYY-MM-DD");
  const nextUp = useMemo(() => holidays.find((h) => h.observed_date >= today), [holidays, today]);
  const past = useMemo(() => holidays.filter((h) => h.observed_date < today).length, [holidays, today]);
  const upcoming = holidays.length - past;

  const openNew = () => { setForm(EMPTY_CLINIC); setEditing("new"); };
  const openEdit = (row) => {
    setForm({
      name: row.name, description: row.description || "",
      start_date: row.start_date, end_date: row.end_date || row.start_date,
      category: row.category || "other", color: row.color || "#7E57C2",
      closes_clinic: row.closes_clinic !== false,
    });
    setEditing(row.id);
  };
  const closeEditor = () => { setEditing(null); setForm(EMPTY_CLINIC); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.start_date) return toast.error("Start date is required");
    if (form.end_date && form.end_date < form.start_date) return toast.error("End date must be on or after start date");
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/clinic-holidays", form);
        toast.success(`${form.name} added`);
      } else {
        await api.put(`/clinic-holidays/${editing}`, form);
        toast.success(`${form.name} updated`);
      }
      closeEditor();
      reloadClinic();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Existing appointments will not be affected.`)) return;
    try {
      await api.delete(`/clinic-holidays/${row.id}`);
      toast.success("Clinic holiday deleted");
      reloadClinic();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="holidays-page">
      {!embedded && (
        <PageHeader title="US Federal Holidays"
          subtitle="Official U.S. federal holidays and clinic custom closures, useful for coverage planning and payroll"
          action={<YearPicker year={year} setYear={setYear} />} />
      )}
      {embedded && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-lg font-heading font-bold text-[#3E2723]">US Federal Holidays and Clinic Closures</div>
            <div className="text-xs text-[#7B6157]">Official U.S. federal holidays and clinic custom closures for {year}</div>
          </div>
          <YearPicker year={year} setYear={setYear} />
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <Card className="p-4" data-testid="hol-summary-total">
          <div className="flex items-center gap-2 text-[#7B6157]"><Flag size={14} /> <span className="text-[10px] uppercase font-bold tracking-wide">Total holidays</span></div>
          <div className="mt-2 text-3xl font-heading font-bold text-[#3E2723]">{holidays.length + clinicHolidays.length}</div>
          <div className="text-xs text-[#7B6157]">{holidays.length} federal · {clinicHolidays.length} clinic</div>
        </Card>
        <Card className="p-4" data-testid="hol-summary-upcoming">
          <div className="flex items-center gap-2 text-[#7B6157]"><CalendarDays size={14} /> <span className="text-[10px] uppercase font-bold tracking-wide">Upcoming federal</span></div>
          <div className="mt-2 text-3xl font-heading font-bold text-[#3E2723]">{upcoming}</div>
          <div className="text-xs text-[#7B6157]">{past} already observed</div>
        </Card>
        <Card className="p-4" data-testid="hol-summary-next">
          <div className="flex items-center gap-2 text-[#7B6157]"><PartyPopper size={14} /> <span className="text-[10px] uppercase font-bold tracking-wide">Next up</span></div>
          {nextUp ? (
            <>
              <div className="mt-2 text-lg font-heading font-bold text-[#3E2723] leading-tight">{nextUp.name}</div>
              <div className="text-xs text-[#7B6157]">{dayjs(nextUp.observed_date).format("dddd, MMM D, YYYY")} · in {Math.max(0, dayjs(nextUp.observed_date).diff(today, "day"))} day{Math.abs(dayjs(nextUp.observed_date).diff(today, "day")) === 1 ? "" : "s"}</div>
            </>
          ) : (
            <div className="mt-2 text-sm text-[#7B6157]">All federal holidays for {year} have passed. Try {year + 1} to plan ahead.</div>
          )}
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between border-b border-[#F1EEE7] px-4 py-2">
          <div className="flex items-center gap-2 text-sm font-bold text-[#3E2723]">
            <Flag size={14} className="text-[#8D5A00]" /> US Federal Holidays ({year})
          </div>
        </div>
        {loading ? (
          <Empty text="Loading holidays…" />
        ) : holidays.length === 0 ? (
          <Empty text="No holidays returned for this year." />
        ) : (
          <Table headers={["Holiday", "Date", "Observed", "Day", "Rule"]}>
            {holidays.map((h) => {
              const isToday = h.observed_date === today;
              const past_ = h.observed_date < today;
              return (
                <tr key={h.date} data-testid={`hol-row-${h.date}`}
                  className={isToday ? "bg-[#FFF8E1]" : past_ ? "opacity-60" : ""}>
                  <Td>
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 rounded-full bg-[#F5F2EB] p-1 text-[#7B6157]"><Flag size={11} /></span>
                      <div>
                        <div className="font-semibold text-[#3E2723]">{h.name}</div>
                        <div className="mt-0.5 text-[11px] text-[#7B6157] max-w-lg">{h.description}</div>
                      </div>
                    </div>
                  </Td>
                  <Td><div className="tabular-nums font-semibold">{dayjs(h.date).format("MMM D, YYYY")}</div></Td>
                  <Td>
                    <div className="tabular-nums">{dayjs(h.observed_date).format("MMM D, YYYY")}</div>
                    {h.observed_date !== h.date && (
                      <div className="mt-0.5 flex items-center gap-1 text-[10px] text-[#8D5A00]">
                        <Info size={10} /> Observed shifted from {dayName(h.date)}
                      </div>
                    )}
                    {isToday && <div className="mt-0.5 text-[10px] font-bold text-[#00695C]">Today</div>}
                  </Td>
                  <Td>{dayName(h.observed_date)}</Td>
                  <Td><Badge value={h.rule} /></Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between border-b border-[#F1EEE7] px-4 py-2">
          <div className="flex items-center gap-2 text-sm font-bold text-[#3E2723]">
            <Building2 size={14} className="text-[#7E57C2]" /> Clinic Custom Holidays ({year})
          </div>
          {isAdmin && (
            <Btn data-testid="clinic-holiday-add" onClick={openNew} className="!h-8 !px-3 !text-xs">
              <Plus size={12} /> New Clinic Holiday
            </Btn>
          )}
        </div>
        {clinicHolidays.length === 0 ? (
          <Empty text={isAdmin ? 'No clinic custom holidays yet. Click "New Clinic Holiday" to add one (e.g., Cesar Chavez Day, staff retreat, cultural observance).' : "No clinic custom holidays configured for this year."} />
        ) : (
          <Table headers={["Name", "Window", "Category", "Closes Clinic", isAdmin ? "Actions" : "Notes"]}>
            {clinicHolidays.map((row) => (
              <tr key={row.id} data-testid={`clinic-holiday-row-${row.id}`}>
                <Td>
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 h-3 w-3 rounded-full shrink-0" style={{ background: row.color || "#7E57C2" }} />
                    <div>
                      <div className="font-semibold text-[#3E2723]">{row.name}</div>
                      {row.description && <div className="mt-0.5 text-[11px] text-[#7B6157] max-w-lg">{row.description}</div>}
                    </div>
                  </div>
                </Td>
                <Td>
                  <div className="tabular-nums">{dayjs(row.start_date).format("MMM D")}{row.end_date && row.end_date !== row.start_date ? ` - ${dayjs(row.end_date).format("MMM D, YYYY")}` : `, ${dayjs(row.start_date).format("YYYY")}`}</div>
                  <div className="text-[10px] text-[#7B6157]">{dayName(row.start_date)}</div>
                </Td>
                <Td><Badge value={row.category || "other"} /></Td>
                <Td>{row.closes_clinic === false ? <span className="text-[11px] text-[#7B6157]">Info only</span> : <span className="text-[11px] font-semibold text-[#8D5A00]">Yes</span>}</Td>
                <Td>
                  {isAdmin ? (
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(row)} data-testid={`clinic-holiday-edit-${row.id}`}
                        className="rounded-full bg-[#F5F2EB] px-2 py-1 text-[10px] font-semibold text-[#4E342E] hover:bg-[#EFEBE0]" aria-label={`Edit ${row.name}`}>
                        <Pencil size={10} /> Edit
                      </button>
                      <button type="button" onClick={() => del(row)} data-testid={`clinic-holiday-delete-${row.id}`}
                        className="rounded-full bg-[#FFEBEE] px-2 py-1 text-[10px] font-semibold text-[#C62828] hover:bg-[#FFCDD2]" aria-label={`Delete ${row.name}`}>
                        <Trash2 size={10} /> Delete
                      </button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-[#84675B]">{row.created_by_name || "-"}</span>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <SlidePanel open={!!editing} onClose={closeEditor}
        title={editing === "new" ? "New Clinic Holiday" : "Edit Clinic Holiday"}
        subtitle="Add a per-clinic non-federal closure or observance"
        testId="clinic-holiday-editor">
        <div className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input data-testid="ch-name" placeholder="e.g., Cesar Chavez Day, Staff Retreat, Diwali"
              value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
          </div>
          <div>
            <Label>Description</Label>
            <textarea data-testid="ch-description" rows={2}
              className="w-full rounded-xl border border-[#EFEBE0] bg-white p-2 text-sm outline-none focus:ring-2 focus:ring-[#8D6E63]"
              value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              placeholder="Short note that appears on the calendar tooltip" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date *</Label>
              <Input type="date" data-testid="ch-start-date" value={form.start_date}
                onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} />
            </div>
            <div><Label>End Date</Label>
              <Input type="date" data-testid="ch-end-date" value={form.end_date}
                onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))} />
              <div className="mt-1 text-[10px] text-[#7B6157]">Same as start for single-day closures</div>
            </div>
            <div><Label>Category</Label>
              <Select data-testid="ch-category" value={form.category}
                onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v} data-testid={`ch-category-option-${v}`}>{l}</option>)}
              </Select>
            </div>
            <div><Label>Chip Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="ch-color" value={form.color}
                  onChange={(e) => setForm((s) => ({ ...s, color: e.target.value }))}
                  className="h-9 w-14 rounded-lg border border-[#EFEBE0] bg-white p-1" />
                <Input data-testid="ch-color-hex" value={form.color}
                  onChange={(e) => setForm((s) => ({ ...s, color: e.target.value }))}
                  placeholder="#7E57C2" className="flex-1" />
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#4E342E]">
            <input type="checkbox" data-testid="ch-closes-clinic" checked={form.closes_clinic}
              onChange={(e) => setForm((s) => ({ ...s, closes_clinic: e.target.checked }))} />
            <span>Clinic is closed on this day (unchecked means info-only chip)</span>
          </label>
          <div className="flex items-center justify-between gap-3 border-t border-[#EFEBE0] pt-4">
            <div className="text-[11px] text-[#7B6157]">Chips render across every calendar view once saved.</div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={closeEditor}>Cancel</Btn>
              <Btn data-testid="ch-save" onClick={save} disabled={busy}>
                {busy ? "Saving…" : (editing === "new" ? "Add Holiday" : "Save Changes")}
              </Btn>
            </div>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
}

function YearPicker({ year, setYear }) {
  return (
    <div className="flex items-center gap-2">
      <Btn variant="outline" data-testid="hol-year-prev" onClick={() => setYear((y) => y - 1)} aria-label="Previous year"><ChevronLeft size={14} /></Btn>
      <div className="rounded-full bg-white border border-[#EFEBE0] px-3 py-1 text-sm font-bold text-[#3E2723]" data-testid="hol-year-label">{year}</div>
      <Btn variant="outline" data-testid="hol-year-next" onClick={() => setYear((y) => y + 1)} aria-label="Next year"><ChevronRight size={14} /></Btn>
      <Btn variant="ghost" data-testid="hol-year-today" onClick={() => setYear(dayjs().year())}>Today</Btn>
    </div>
  );
}
